import { SELECTED_TEAM_ID_KEY } from '@dc-shared-web/lib/constants'
import { DocumentSearchResult } from '@/dc-shared-web/src'
import {
  DOCUMENTS_QUERY_KEY,
  LIST_QUERY_KEY,
} from '@/dc-shared-web/src/hooks/api/queryKeys'
import { useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { InfiniteData, useQueryClient } from 'react-query'

export const useDocumentCount = (
  currentTable: 'dashboard' | 'archived' | 'shared'
) => {
  const queryClient = useQueryClient()
  const [documentCount, setDocumentCount] = useState<number>(0)
  const searchParams = useSearchParams()
  //default this to an empty string, since that is how it gets set in the query cache
  //if there is not a teamId. Otherwise, this will be null and will not match any query
  const teamId = searchParams.get(SELECTED_TEAM_ID_KEY) || ''

  useEffect(() => {
    const getCount = () => {
      const queries = queryClient.getQueriesData<
        InfiniteData<{ count: number; data: DocumentSearchResult[] }>
      >([
        DOCUMENTS_QUERY_KEY,
        LIST_QUERY_KEY,
        currentTable,
        { currentTeamId: teamId },
      ])

      // Find the query that matches the current teamId
      const matchingQuery = queries.find(([queryKey]) => {
        const params = queryKey[3] as { currentTeamId?: string }
        return params?.currentTeamId === teamId
      })
      // queries[0] is the queryKey
      // queries[1] is the data
      // queries[1]?.pages?.[0]?.count is the value of the count property
      // on the first page of data for the first matching query
      return matchingQuery?.[1]?.pages?.[0]?.count ?? 0
    }

    // Subscribe only to changes for the current table's query
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event?.query.queryKey[2] === currentTable) {
        requestAnimationFrame(() => {
          setDocumentCount(getCount())
        })
      }
    })

    // Set initial count
    setDocumentCount(getCount())

    return () => {
      unsubscribe()
    }
  }, [currentTable, queryClient, teamId])

  return documentCount
}
